'use client';

import { Ref, useEffect, useImperativeHandle, useMemo } from 'react';

import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Dropdown, DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { Controller, useForm, useWatch } from 'react-hook-form';

import { usePathCountryCode } from '../../../../../../../hooks/use-path-locale';
import { LeadTimeCheckCriteria, hasMtsLocalLeadTime } from '../../../../../../../utils/lead-time';
import { RequestForQuotationStep } from '../../../../models/request-for-quotation-step';
import { RfqTimeframeOptions } from '../fields/rfq-timeframe-options';

export interface FormValue {
   country: DropdownOption;
   city: string;
   postalCode: string;
   timeFrame: DropdownOption;
}

export interface LocalFormValue {
   country: DropdownOption | undefined;
   city: string;
   postalCode: string;
   timeFrame: DropdownOption | undefined;
}

interface Props {
   stepRef: Ref<RequestForQuotationStep>;
   countries: { fullName: string; shortName: string }[] | undefined;
   defaultFormValue: FormValue | undefined;
   leadTimeIndicators?: LeadTimeIndicator[];
   selectedColor?: { colorName: string; producerColorCode: string | undefined; colorId?: number };
   isLeadTimeEnabled?: boolean;
   packagingGroupId?: string;
   onSubmit: (value: FormValue) => void;
}

export const RfqFlyoutStepDeliveryArea = ({
   stepRef,
   countries,
   defaultFormValue,
   leadTimeIndicators = [],
   selectedColor,
   isLeadTimeEnabled = false,
   packagingGroupId,
   onSubmit
}: Props) => {
   const translate = useTranslations();
   const countryCode = usePathCountryCode();

   const countryMtsLocalMap = useMemo(() => {
      if (!isLeadTimeEnabled || !selectedColor?.colorId || Number.isNaN(Number(packagingGroupId))) return {};
      return Object.fromEntries(
         (countries ?? []).map((country) => {
            const criteria: LeadTimeCheckCriteria = {
               colorId: selectedColor.colorId!,
               producerColorCode: selectedColor.producerColorCode,
               packagingGroupId: packagingGroupId ? Number(packagingGroupId) : undefined,
               countryCode: country.shortName
            };
            return [country.shortName, hasMtsLocalLeadTime(leadTimeIndicators, criteria, true)];
         })
      );
   }, [countries, leadTimeIndicators, selectedColor, isLeadTimeEnabled, packagingGroupId]);

   const countryOptions: DropdownOption[] = useMemo(() => {
      const options = (countries ?? []).map(
         (country) =>
            ({
               id: country.shortName,
               value: country.fullName
            } as DropdownOption)
      );

      if (isLeadTimeEnabled) {
         const allDeliveryOnRequest = options.every((option) => countryMtsLocalMap[option.id.toUpperCase()] !== true);

         if (!allDeliveryOnRequest) {
            options.forEach((option) => {
               option.badge = {
                  label:
                     countryMtsLocalMap[option.id.toUpperCase()] === true
                        ? translate('request-for-quotation.lead-time.mts-local-days')
                        : translate('request-for-quotation.lead-time.delivery-on-request'),
                  variant: 'info',
                  leadingIcon: 'truck'
               };
            });
         }
      }

      return options.sort((a, b) => a.value.localeCompare(b.value));
   }, [countries, isLeadTimeEnabled, countryMtsLocalMap, packagingGroupId, selectedColor]);

   const { control, handleSubmit, setValue } = useForm<LocalFormValue, unknown, FormValue>({
      defaultValues: defaultFormValue ?? {
         country: countryOptions.find((countryOption) => countryOption.id === countryCode),
         city: '',
         postalCode: '',
         timeFrame: undefined
      }
   });

   const selectedCountry = useWatch({
      control,
      name: 'country'
   });

   // Update selected country badge when color/packaging changes
   useEffect(() => {
      if (selectedCountry && selectedColor) {
         const updatedCountry = countryOptions.find((option) => option.id === selectedCountry.id);
         if (updatedCountry && updatedCountry.badge?.label !== selectedCountry.badge?.label) {
            setValue('country', updatedCountry);
         }
      }
   }, [selectedColor, countryOptions, selectedCountry, setValue]);

   useImperativeHandle(stepRef, () => ({
      submit: async () => {
         await handleSubmit((value) => onSubmit(value))();
      }
   }));

   return (
      <Box direction="column">
         <Box direction="column" gap="md">
            <Typography component="span" variant="body-highlight">
               {translate('page-consumer.request-for-quotation.delivery-area')}
            </Typography>
            <Typography component="span" variant="body">
               {translate('page-consumer.request-for-quotation.delivery-area-description')}
            </Typography>
            <Controller
               control={control}
               name="country"
               rules={{
                  required: translate('form-validation.required', { field: translate('page-consumer.request-for-quotation.contact-info.country') })
               }}
               render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <Dropdown
                     inputRef={ref}
                     {...field}
                     id="rfq-delivery-area-country"
                     label={translate('page-consumer.request-for-quotation.contact-info.country')}
                     emptyOption={false}
                     disabled={!countries}
                     options={countryOptions}
                     error={!!error}
                     errorMessage={error?.message}
                  />
               )}
            />

            <Box gap="xs">
               <Controller
                  control={control}
                  name="city"
                  rules={{
                     required: translate('form-validation.required', { field: translate('form-field.city') }),
                     maxLength: {
                        value: 40,
                        message: translate('form-validation.max-length', { field: translate('form-field.city'), value: 40 })
                     }
                  }}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                     <InputField
                        inputRef={ref}
                        {...field}
                        label={translate('page-consumer.request-for-quotation.city')}
                        id="rfq-delivery-area-city"
                        error={!!error}
                        errorMessage={error?.message}
                     />
                  )}
               />
               <Controller
                  control={control}
                  name="postalCode"
                  rules={{
                     required: translate('form-validation.required', { field: translate('form-field.zip-postal-code') }),
                     maxLength: {
                        value: 10,
                        message: translate('form-validation.max-length', {
                           field: translate('form-field.zip-postal-code'),
                           value: 10
                        })
                     }
                  }}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                     <InputField
                        inputRef={ref}
                        {...field}
                        label={translate('page-consumer.request-for-quotation.zip-postal-code')}
                        id="rfq-delivery-area-zip"
                        error={!!error}
                        errorMessage={error?.message}
                     />
                  )}
               />
            </Box>
         </Box>
         <RfqTimeframeOptions control={control} name="timeFrame" />
      </Box>
   );
};
